import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import {
  bulkImportExchangeRateParts,
  createExchangeRateOEMOtherProcess,
  createExchangeRatePart,
  createExchangeRateSpareOtherProcess,
  deleteExchangeRateOEMOtherProcess,
  deleteExchangeRateSpareOtherProcess,
  getExchangeRateMaster,
  getExchangeRatePartHistory,
  getExchangeRateParts,
  getExchangeRateProcess,
  updateExchangeRateOEMOtherProcess,
  updateExchangeRatePart,
  updateExchangeRateSpareOtherProcess,
  type exchangeRateBody,
  type exchangeRateBulkImportItem,
  type exchangeRateProcessBody,
} from "@/api/breakdownExchangeRateService";

export const useGetBreakdownExchangeRateMaster = () => {
  return useQuery({
    queryKey: queryKeys.breakdownExchangeRate.master(),
    queryFn: () => getExchangeRateMaster(),
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useGetBreakdownExchangeRatePart = (
  supplierCode: string,
  isActive: boolean,
) => {
  return useQuery({
    queryKey: queryKeys.breakdownExchangeRate.parts(supplierCode, isActive),
    queryFn: () => getExchangeRateParts(supplierCode, isActive),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!supplierCode,
  });
};

export const useCreateBreakdownExchangeRatePart = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationFn: ({
      supplierCode,
      data,
    }: {
      supplierCode: string;
      data: exchangeRateBody;
    }) => createExchangeRatePart(supplierCode, data),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useUpdateBreakdownExchangeRatePart = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationFn: ({
      supplierCode,
      data,
    }: {
      supplierCode: string;
      data: exchangeRateBody[];
    }) => updateExchangeRatePart(supplierCode, data),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useBulkImportBreakdownExchangeRateParts = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.bulkImportParts(),
    mutationFn: ({
      supplierCode,
      data,
    }: {
      supplierCode: string;
      data: exchangeRateBulkImportItem[];
    }) => bulkImportExchangeRateParts(supplierCode, data),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRates();
    },
  });
};

export const useGetBreakdownExchangeRatePartHistory = (partId: number) => {
  return useQuery({
    queryKey: queryKeys.breakdownExchangeRate.partHistory(partId),
    queryFn: () => getExchangeRatePartHistory(partId),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!partId,
  });
};

export const useGetBreakdownExchangeRateProcess = (supplierCode: string) => {
  return useQuery({
    queryKey: queryKeys.breakdownExchangeRate.process(supplierCode),
    queryFn: () => getExchangeRateProcess(supplierCode),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: !!supplierCode,
  });
};

export const useCreateBreakdownExchangeRateOEMProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.createOEMOtherProcess(),
    mutationFn: (params: exchangeRateProcessBody) =>
      createExchangeRateOEMOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};

export const useCreateBreakdownExchangeRateSpareProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.createSpareOtherProcess(),
    mutationFn: (params: exchangeRateProcessBody) =>
      createExchangeRateSpareOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};

export const useUpdateBreakdownExchangeRateOEMProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.updateOEMOtherProcess(),
    mutationFn: (params: exchangeRateProcessBody) =>
      updateExchangeRateOEMOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};

export const useUpdateBreakdownExchangeRateSpareProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.updateSpareOtherProcess(),
    mutationFn: (params: exchangeRateProcessBody) =>
      updateExchangeRateSpareOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};

export const useDeleteBreakdownExchangeRateOEMProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.deleteOEMOtherProcess(),
    mutationFn: (id: number) => {
      return deleteExchangeRateOEMOtherProcess(id);
    },
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};

export const useDeleteBreakdownExchangeRateSpareProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdownExchangeRate.deleteSpareOtherProcess(),
    mutationFn: (id: number) => {
      return deleteExchangeRateSpareOtherProcess(id);
    },
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownExchangeRateProcess();
    },
  });
};
